
import { useState, useMemo } from "react";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
}

export const useProductFiltering = (products: Product[] | undefined) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [posStatusFilter, setPosStatusFilter] = useState<"all" | "found" | "not-found">("all");

  // Ensure products is always an array to prevent filter errors
  const safeProducts = useMemo(() => {
    return Array.isArray(products) ? products : [];
  }, [products]);

  // Filter products based on search query and POS status with stable dependencies
  const filteredProducts = useMemo(() => {
    console.log('🔍 Filtering products:', {
      totalProducts: safeProducts.length,
      searchQuery,
      posStatusFilter
    });
    
    if (safeProducts.length === 0) {
      return [];
    }

    // Optimize filtering for large datasets
    if (safeProducts.length > 1000) {
      console.log('⚡ Using optimized filtering for large dataset');
    }
    
    return safeProducts.filter(product => {
      // Early return for status filter to improve performance
      if (posStatusFilter !== "all") {
        if (posStatusFilter === "found" && product.posStatus !== "found") return false;
        if (posStatusFilter === "not-found" && product.posStatus !== "not-found") return false;
      }
      
      // Search filter optimization
      if (searchQuery) {
        const lowerQuery = searchQuery.toLowerCase();
        const nameMatch = product.name.toLowerCase().includes(lowerQuery);
        const barcodeMatch = product.barcode.includes(searchQuery);
        return nameMatch || barcodeMatch;
      }
      
      return true;
    });
  }, [safeProducts, searchQuery, posStatusFilter]);

  return {
    searchQuery,
    setSearchQuery,
    posStatusFilter,
    setPosStatusFilter,
    filteredProducts
  };
};
